import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Paper,
  Avatar,
  Divider,
  Fade,
  CircularProgress,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Business, Login } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_HOST } from '../../Services/baseUrl';

// https://stackoverflow.com/questions/25774999/nginx-stat-failed-13-permission-denied

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({
    count: 0,
    company: '',
    months: [],
    credits: {
      total: 0,
      used: 0,
      left: 0,
    },
  });
  const [loading, setLoading] = useState(false);

  const [showTabs, setShowTabs] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [recentReports, setRecentReports] = useState([]);
  const navigate = useNavigate();


  const handleLogin = async () => {
    setLoading(true);
    try {
      if (username === 'admin@toothlens.com' && password === 'Toothlens@2025') {
        navigate('/report-count/dashboard');
        return;
      }
      const loginRes = await axios.post(`${API_HOST}third-party-reports/simmetry-ml/count`, {
        company: username,
        access_key: password,
      });

      if (loginRes.status === 200) {
        
        setReportData({
          count: loginRes?.data?.data?.totalCount || 0,
          company: username,
          months: loginRes?.data?.data?.monthlyCounts || [],
          credits: {
            total: loginRes?.data?.data?.credits?.total || 0,
            used: loginRes?.data?.data?.credits?.used || 0,
            left: loginRes?.data?.data?.credits?.left || 0,
          },
        });

        setRecentReports(loginRes?.data?.data?.records || []);
        setDialogOpen(true);
        setShowTabs(true);
      }
    } catch (error) {
      alert('Invalid credentials or server error.');
    } finally {
      setLoading(false);
    }
  };

  const formatUTC = (dateStr) => {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateStr));
  };

  const downloadCSV = async () => {
    setLoading(true);
    try {
      const allReports = await axios.post(`${API_HOST}third-party-reports/all-reports`, { company: username });

      if (!allReports || allReports?.data?.data.length === 0) return;

      const rows = allReports?.data?.data.map((record) => {
        const date = new Date(record.createdAt);
        return {
          uids: record.uid || record.tpid,
          created_date: date.toISOString().split('T')[0], // YYYY-MM-DD
          time: date.toISOString().split('T')[1].split('.')[0] + ' UTC', // HH:mm:ss UTC
        };
      });

      const header = ['uids', 'created_date', 'time'];
      const csvContent = [
        header.join(','),
        ...rows.map((r) => `${r.uids},${r.created_date},${r.time}`),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'recent-reports.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);


    } catch (error) {
      console.error('Failed to download full report:', error);
      alert('Unable to fetch all records');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{ background: '#f0f2f5' }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          width: 350,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
          <Login />
        </Avatar>

        <Typography variant="h6" gutterBottom>
          Sign in to Your Account
        </Typography>

        <TextField
          label="Username"
          variant="outlined"
          fullWidth
          margin="normal"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />

        <TextField
          label="Password"
          variant="outlined"
          fullWidth
          margin="normal"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2, borderRadius: 2 }}
          onClick={handleLogin}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>

      </Paper>

      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setLoading(false)
      }} maxWidth="sm" fullWidth>
        <DialogContent
          sx={{
            py: 4,
            px: 3,
            borderRadius: 3,
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mb: 1 }}>
              <Business fontSize="large" />
            </Avatar>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Welcome, {reportData.company}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Here's your latest report summary
            </Typography>
          </Box>

          <Tabs
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(newValue)}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 3 }}
          >
            <Tab label="📊 Summary" />
            <Tab label="📋 Recent Records" />
          </Tabs>

          {/* Tab 1: Summary */}
          {tabIndex === 0 && (
            <Box>
              <Box
                sx={{
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderRadius: 2,
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="subtitle2" fontWeight={500}>
                  Total Reports
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {reportData.count}
                </Typography>
              </Box>

              {/* Credits Summary */}
              {
                reportData.credits.total > 0 && (
                  <Box
                    sx={{
                      bgcolor: '#eef2f7',
                      p: 2,
                      mt: 3,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                      Credit Usage
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total
                        </Typography>
                        <Typography fontWeight={600}>{reportData.credits.total}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Used
                        </Typography>
                        <Typography fontWeight={600}>{reportData.credits.used}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Left
                        </Typography>
                        <Typography fontWeight={600} color={reportData.credits.left <= 0 ? 'error.main' : 'success.main'}>
                          {reportData.credits.left}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )
              }


              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Last 6 Months
              </Typography>

              <Grid container spacing={1}>
                {reportData.months?.map((m) => (
                  <Grid item xs={6} sm={4} key={m.month}>
                    <Box
                      sx={{
                        bgcolor: '#f5f5f5',
                        p: 1,
                        borderRadius: 1.5,
                        textAlign: 'center',
                      }}
                    >
                      <Typography fontWeight={600}>{m.month}</Typography>
                      <Typography>{m.count}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Tab 2: Recent Records */}
          {tabIndex === 1 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Latest 20 Records
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {recentReports.length === 0 ? (
                  <Typography>No recent records found.</Typography>
                ) : (
                  recentReports.map((record, index) => (
                    <Box
                      key={record.uid || index}
                      sx={{
                        bgcolor: '#f9f9f9',
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        #{index + 1} — UID: {record.uid || record.tpid}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatUTC(record.createdAt)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
              <Button
                onClick={downloadCSV}
                variant="outlined"
                size="small"
                sx={{ mb: 2, borderRadius: 2 }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </Box>
          )}

          <Box mt={4} textAlign="center">
            <Button
              onClick={() => setDialogOpen(false)}
              variant="contained"
              color="primary"
              sx={{ px: 5, borderRadius: 2 }}
            >
              Close
            </Button>
            {/* <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            color="primary"
            sx={{ mt: 3, borderRadius: 2 }}
          >
            Close
          </Button> */}
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default LoginPage;
